import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  FormControlLabel, 
  Checkbox, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  FormHelperText,
  Alert,
  Snackbar,
  Paper,
  Grid,
  CircularProgress
} from '@mui/material';
import waitlistService from '../firebase/waitlistService';

const businessTypes = [
  'Plumbing',
  'Electrical',
  'HVAC',
  'Handyman',
  'Other'
];

const jobVolumes = [
  '1-5',
  '6-15',
  '16-30',
  '31+'
];

const WaitlistForm = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    fullName: '',
    phoneNumber: '',
    email: '',
    businessType: '',
    challenges: '',
    jobVolume: '',
    contactConsent: false,
    termsAgreement: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!formData.fullName.trim()) newErrors.fullName = 'Your name is required';
    
    // Phone validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Business type validation
    if (!formData.businessType) newErrors.businessType = 'Please select your business type';
    
    // Terms agreement
    if (!formData.termsAgreement) newErrors.termsAgreement = 'You must agree to the terms and privacy policy';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // State for beta statistics
  const [betaStats, setBetaStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Fetch beta stats on component mount
  useEffect(() => {
    const fetchBetaStats = async () => {
      try {
        const stats = await waitlistService.getBetaStats();
        setBetaStats(stats);
      } catch (error) {
        console.error('Error fetching beta stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchBetaStats();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Use waitlistService to add entry
      await waitlistService.addWaitlistEntry({
        businessName: formData.businessName,
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        businessType: formData.businessType,
        challenges: formData.challenges,
        jobVolume: formData.jobVolume,
        contactConsent: formData.contactConsent,
        termsAgreement: formData.termsAgreement
      });
      
      // Reset form
      setFormData({
        businessName: '',
        fullName: '',
        phoneNumber: '',
        email: '',
        businessType: '',
        challenges: '',
        jobVolume: '',
        contactConsent: false,
        termsAgreement: false
      });
      
      setShowSuccess(true);
      
      // Refresh beta stats after successful submission
      const updatedStats = await waitlistService.getBetaStats();
      setBetaStats(updatedStats);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(error.message || 'An error occurred. Please try again.');
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h2" gutterBottom>
        Join the Beta Program
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 1 }}>
        Be one of the first 100 to get early access to our beta program.
      </Typography>
      
      {loadingStats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : betaStats ? (
        <Box sx={{ mb: 3, mt: 2 }}>
          <Alert severity="warning" icon={false} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Only {betaStats.spotsRemaining} spots remaining!
            </Typography>
            <Typography variant="body2">
              Registration closes on {new Date(betaStats.registrationCloses.seconds * 1000).toLocaleDateString()}
            </Typography>
          </Alert>
        </Box>
      ) : null}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Business Name"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              error={!!errors.businessName}
              helperText={errors.businessName}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Your Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              error={!!errors.fullName}
              helperText={errors.fullName}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={!!errors.businessType} margin="normal">
              <InputLabel id="business-type-label">Business Type</InputLabel>
              <Select
                labelId="business-type-label"
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                label="Business Type"
              >
                {businessTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
              {errors.businessType && <FormHelperText>{errors.businessType}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="job-volume-label">Monthly Jobs</InputLabel>
              <Select
                labelId="job-volume-label"
                name="jobVolume"
                value={formData.jobVolume}
                onChange={handleChange}
                label="Monthly Jobs"
              >
                {jobVolumes.map((volume) => (
                  <MenuItem key={volume} value={volume}>{volume}</MenuItem>
                ))}
              </Select>
              <FormHelperText>How many jobs do you handle monthly?</FormHelperText>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Current Business Challenges"
              name="challenges"
              multiline
              rows={3}
              value={formData.challenges}
              onChange={handleChange}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="contactConsent"
                  checked={formData.contactConsent}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="I want to be contacted for beta access"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  required
                  name="termsAgreement"
                  checked={formData.termsAgreement}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="I agree to the terms and privacy policy"
            />
            {errors.termsAgreement && (
              <FormHelperText error>{errors.termsAgreement}</FormHelperText>
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={isSubmitting}
              sx={{ 
                mt: 2, 
                py: 1.5, 
                px: 4,
                fontSize: '1.1rem',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Join the Beta Program'}
            </Button>
            
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              We'll contact you within 24 hours to get you set up.
            </Typography>
          </Grid>
        </Grid>
      </Box>
      
      {/* Success message */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Thank you for joining our beta program! We'll contact you within 24 hours.
        </Alert>
      </Snackbar>
      
      {/* Error message */}
      <Snackbar 
        open={showError} 
        autoHideDuration={6000} 
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowError(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default WaitlistForm;
